import React, { forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './TimeInput.scss';
import { format } from 'date-fns';
import moment from 'moment';
import { combineDateAndTime } from '../../../utils/CombineDateAndTime';

interface CustomProps {
  default?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  gap?: 'sm' | 'md' | 'lg';
  name?: string;
  label?: string;
  error?: string;
  minDate?: Date;
  maxDate?: Date;
  filterDate?: (date: Date) => boolean;
  disabled?: boolean;
  startTime?: string;
  endTime?: string;
  avoidToday?: boolean;
  range?: string
  secondTime?: boolean
}

interface CustomState {
  value?: Date;
}

class DateInput extends React.Component<CustomProps, CustomState> {
  static defaultProps: Partial<CustomProps> = {
    gap: 'md',
    name: '',
    label: '',
    error: '',
    placeholder: '',
  };
  constructor(props: CustomProps) {
    super(props);
    this.state = {
      value: this.props.default
        ? this.createDate(this.props.default)
        : undefined,
    };

    this.handleChange = this.handleChange.bind(this);
  }


  componentDidMount() {
    this.state = {
      value: this.props.default
        ? this.createDate(this.props.default)
        : undefined,
    };
  }

  componentDidUpdate(prevProps: CustomProps) {
    if (prevProps.default !== this.props.default) {
      this.updateDefault();
    }
  }

  updateDefault() {
    this.setState({
      value: this.props.default
        ? this.createDate(this.props.default)
        : undefined,
    });
  }

  createDate(value: string) {
    const date = new Date();

    let hours = Number(value?.match(/^(\d+)/)![1]);
    const minutes = Number(value.match(/:(\d+)/)![1]);
    const AMPM = value.match(/\s(.*)$/)![1];

    if (AMPM.toUpperCase() === 'PM' && hours < 12) hours = hours + 12;
    if (AMPM.toUpperCase() === 'AM' && hours === 12) hours = hours - 12;

    date.setHours(hours);
    date.setMinutes(minutes);

    return date;
  }

  handleChange(date: Date) {
    this.setState({
      value: date,
    });

    this.props.onChange(moment(date).format('hh:mm a'));
  }

  render() {
    const {
      name,
      label,
      placeholder,
      error,
      disabled,
      minDate,
      maxDate,
      range
    } = this.props;
    const inputIdCandidate = name || label || placeholder || '';
    const inputId = inputIdCandidate.toLowerCase().split(' ').join('-');

    const calculateMinTime = (date: Date) => {
      if (this.props.startTime) {
        if (this.props.secondTime) {
          // Add 5 minutes if secondTime is true
          date = combineDateAndTime(date, this.props.startTime);
          date = moment(date).add(5, 'minutes').toDate();
          return moment(date).toDate();
        }
        date = combineDateAndTime(date, this.props.startTime);
        return moment(date).toDate();
      }
      // Set minimum time to 9:00 AM
      return moment(date).hour(9).minute(0).toDate();
    };

    const calculateMaxTime = (date: Date) => {
      if (this.props.endTime) {
        date = combineDateAndTime(date, this.props.endTime);
        return moment(date).toDate();
      }

      // Set maximum time to 9:00 PM
      return moment(date).hour(21).minute(0).toDate();
    };

    const timeClassName = (time: Date) => {
      if (range === "8-8") {
        const hour = time.getHours();
        const minute = time.getMinutes();

        // Convert this.props.startTime and this.props.endTime to moments for comparison
        const startMoment = moment(this.props.startTime, 'hh:mm A');
        const endMoment = moment(this.props.endTime, 'hh:mm A');

        // Convert the current time to a moment for comparison
        const currentMoment = moment({ hour, minute });

        // Show times after the this.props.startTime and after 5 minutes if secondTime is true
        if (currentMoment.isBefore(startMoment) || (this.props.secondTime && currentMoment.isBefore(startMoment.add(5, 'minutes'))) || currentMoment.isAfter(endMoment)) {
          return "hide-time";
        }

        return null;
      } else {
        return null
      }
    };

    return (
      <div className="input-group">
        <DatePicker
          id={inputId}
          selected={this.state.value}
          onChange={this.handleChange}
          placeholderText={placeholder}
          dropdownMode="select"
          disabled={disabled}
          showTimeSelect
          showTimeSelectOnly
          timeIntervals={5}
          timeCaption="Time"
          dateFormat="h:mm aa"
          openToDate={calculateMinTime(
            minDate ? new Date(minDate) : new Date()
          )}
          minTime={calculateMinTime(
            minDate ? new Date(minDate) : new Date()
          )}
          maxTime={calculateMaxTime(
            maxDate ? new Date(maxDate) : new Date()
          )}
          customInput={<CustomDateInput />}
          timeClassName={timeClassName}
        />

        {error && <span className="Input__error">{error}</span>}
      </div>
    );
  }
}
export default DateInput;

const CustomDateInput = forwardRef((props, ref) => {
  const { id, placeholder, value, onClick }: { [key: string]: any } = props;
  return (
    <div>
      <input
        id={id}
        className="form-control rounded-sm font-weight-semibold"
        placeholder={placeholder}
        onClick={onClick}
        value={value}
        autoComplete="off"
      />
    </div>
  );
});
